import Vue from 'vue';
import Router from 'vue-router';
import qs from 'qs';

import Campaigns from '@/Campaigns/router';
import Contacts from '@/Contacts/router';
import EmailIntegrations from '@/Email-Integrations/router';
import Onboarding from '@/Onboarding/router';
import ResendVerificationEmail from '@/ResendVerificationEmail/router';
import Reporting from '@/Reporting/router';
import Tasks from '@/Tasks/router';
import Media from '@/Media/router';
import Core from '@/Core/router';
import Communications from '@/Communications/router';
import scrollBehavior from '@/Core/router/scroll-behavior';
import Marketing from '@/Marketing/router';
import MarketingSSO from '@/Authentication/marketing-sso/router';
import Documents from '@/Documents/router';
import A2P from '@/a2p/router';
import LeadsplusReport from '@/Leadsplus/router'
import AccountManagement from '@/AccountManagement/router';
import SunsetAdmin from '@/SunsetAdmin/router';

import userInformation from '@/Core/store/user-information';
import Store from '@/store';

import { validate } from '@/Authentication/api';

const UnsubscribeEmail = () => import(/* webpackChunkName: 'Unsubscribe' */ '@/Email/Unsubscribe');

Vue.use(Router);

const router = new Router({
  mode: 'history',
  scrollBehavior,

  /**
   * Use the qs package to parse & stringify
   * to support nested objects for feathers support:
   *
   * {
   *   created_at: {
   *     $gt: '2018-01-01T00:00:00.000Z'
   *   }
   * }
   * => /contacts?created_at[$gt]=2018-01-01T00:00:00.000Z
   *
   * Without these overrides, the router fails to interpret
   * the nested query object:
   * {
   *   created_at: {
   *     $gt: '2018-01-01T00:00:00.000Z'
   *   }
   * }
   * => /contacts?created_at=Object
   *
   * @param  {string} query - a query string to parses
   * @return {object} - an object formed from the query string
   */
  parseQuery(query) {
    return qs.parse(query);
  },
  // See comments above
  stringifyQuery(query) {
    const result = qs.stringify(query);
    return result ? `?${result}` : '';
  },

  routes: [
    {
      path: '/',
      redirect: { name: 'sunset' },
    },
    {
      path: '/login',
      beforeEnter() {
        window.location.href = process.env.VUE_APP_ACCOUNTS_APP_URL;
      },
    },
    {
      path: '/communications',
      name: 'communications',
      redirect: { name: 'communications.email' },
    },
    {
      path: '/daily-mail-unsubscribe',
      name: 'daily-mail-unsubscribe',
      component: UnsubscribeEmail,
      meta: { auth: true },
    },
    ...Campaigns,
    ...Contacts,
    ...Onboarding,
    ...Reporting,
    ...Tasks,
    ...Communications,
    ...Core,
    ...Media,
    ...Marketing,
    ...MarketingSSO,
    ...EmailIntegrations,
    ...Documents,
    ...A2P,
    ...LeadsplusReport,
    ...AccountManagement,
    ...ResendVerificationEmail,
    ...SunsetAdmin,
  ],
});

/**
 * Clear local storage if there's a new clearing key.
 *
 * Update the timestamp if your feature requires that local storage be cleared
 *  - Set to the current date/time in the format YYYYMMDD-HHmm
 *  - Really, any different value here will prompt cachebusting
 */
function clearLocalStorage() {
  const LOCAL_STORAGE_CLEAR_TIMESTAMP = '20190625-1200';
  const existingLocalStorageTimestamp = localStorage.getItem('localStorageClearVersionKey');

  if (existingLocalStorageTimestamp === LOCAL_STORAGE_CLEAR_TIMESTAMP) return;

  localStorage.clear();
  localStorage.setItem('localStorageClearVersionKey', LOCAL_STORAGE_CLEAR_TIMESTAMP);
}

/**
 * * BEFORE EACH * page change.
 *
 * Redirected unauthenticated users to login page.
 */

// Name of the cookie that allows bypassing the sunset redirect
const SUNSET_BYPASS_COOKIE = 'sunset_bypass';

// Helper to read a cookie value by name
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

router.beforeEach(async (to, from, next) => {
  // Sunset-admin routes are standalone and don't require CRM/Accounts authentication
  if (to.path.startsWith('/sunset-admin')) {
    // If accessing login page, allow access without authentication
    if (to.path === '/sunset-admin/login' || to.path === '/sunset-admin/login/') {
      return next();
    }

    // For all other sunset-admin routes, require sunset-admin authentication
    const isAuthenticated = Store.getters['sunsetAdmin/isAuthenticated'];
    if (!isAuthenticated) {
      return next({ name: 'sunset-admin-login', replace: true });
    }

    // User is authenticated, allow access
    return next();
  }

  // If the bypass cookie is set, allow normal navigation
  if (getCookie(SUNSET_BYPASS_COOKIE) === '1') {
    // Does this page require authentication?
    let requiresAuth = false;
    to.matched.forEach(route => {
      if (!requiresAuth) requiresAuth = route.meta.auth;
    });

    if (requiresAuth) {
      const valid = await validate();
      if (valid) {
        next();
      }
    } else {
      // no auth required
      next();
    }
  }
  // Get the current user from the store (if loaded)
  const user = userInformation.state.user;
  let isCarUser = false;
  if (user && user.subscriptionOverview && (user.subscriptionOverview.mls_board === 'car' || user.subscriptionOverview.txt_plan === 'car')) isCarUser = true;

  // Force all navigation to the Sunset page while the app is shut down.
  const authorizedUsers = (process.env.VUE_APP_AUTHORIZED_USERS || '').split(',').map(id => parseInt(id, 10));
  if (to.name !== 'sunset' && (!user || !authorizedUsers.includes(user.id)) && !getCookie(SUNSET_BYPASS_COOKIE)) {
    return next({ name: 'sunset', replace: true });
  }

  // Does this page require authentication?
  let requiresAuth = false;
  to.matched.forEach(route => {
    if (!requiresAuth) requiresAuth = route.meta.auth;
  });

  clearLocalStorage();

  if (requiresAuth) {
    const valid = await validate();
    if (valid) {
      next();
    }
  } else {
    // no auth required
    next();
  }
});

router.afterEach((to, from) => {
  /**
   * This must be done here becasue vue-router does not have after route enter hook.
   * All vue-router provides are before route enter hooks, so we have to set the `to` manually,
   * because the user still hasn't entered the desired route.
   * Doing this in the `afterEach` hooks ensures the user has all the pre-requisites
   * to load the page, hence this is as close as getting to when the user loads the route
   */
  const { user } = userInformation.state;
  // distinguish crm/settings and accounts/settings
  if (to.path === 'settings') {
    window.gtag('config', process.env.VUE_APP_GA_TRACKING_ID_CRM, { page_path: 'crm-settings' });
    window.gtag('config', process.env.VUE_APP_GA_TRACKING_ID_ALL, { page_path: 'crm-settings' });
  } else {
    window.gtag('config', process.env.VUE_APP_GA_TRACKING_ID_CRM, { page_path: to.path });
    window.gtag('config', process.env.VUE_APP_GA_TRACKING_ID_ALL, { page_path: to.path });
  }

  if (user) {
    window.gtag('set', { user_id: user.id }); // Set the user ID using signed-in user_id.
  }
});

export default router;
